import { useCallback, useSyncExternalStore } from 'react';

/**
 * Pedidos de delivery feitos SEM LOGIN neste aparelho. Sem conta não existe "Meus
 * pedidos" no servidor, e o UUID do pedido só vivia na URL do acompanhamento: saiu
 * dela, o cliente perdia o pedido. Guardamos só a referência (id, código curto e
 * criação) — status, itens e endereço vêm sempre da API pública do pedido, para não
 * deixar nome e endereço em texto claro num celular que pode ser compartilhado.
 */
export interface PedidoLocal {
  id: string;
  shortId: string | null;
  /** Epoch ms da criação do pedido — base da validade. */
  criadoEm: number;
  /** Já visto em situação final: sai da faixa de "pedido em andamento", segue na lista. */
  finalizado?: boolean;
}

const baseLs = 'cardapio-sischef:pedidos-locais';
const EVENTO_MUDANCA = 'cardapio-sischef:pedidos-locais';

/** Por quanto tempo o pedido fica acessível sem login. */
export const VALIDADE_PEDIDO_LOCAL_MS = 3 * 24 * 60 * 60 * 1000;
/** Teto da lista — o mais antigo sai primeiro. */
export const MAXIMO_PEDIDOS_LOCAIS = 10;

// Por loja, como o carrinho: duas lojas no mesmo domínio não enxergam os pedidos uma da outra.
function chavePorLoja(idConfig: number): string {
  return `${baseLs}:${idConfig}`;
}

function valido(p: unknown): p is PedidoLocal {
  const c = p as PedidoLocal | null;
  return typeof c?.id === 'string' && typeof c.criadoEm === 'number';
}

function ler(idConfig: number): PedidoLocal[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const salvo: unknown = JSON.parse(localStorage.getItem(chavePorLoja(idConfig)) ?? '[]');
    return Array.isArray(salvo) ? salvo.filter(valido) : [];
  } catch {
    return [];
  }
}

function gravar(idConfig: number, pedidos: PedidoLocal[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (pedidos.length === 0) localStorage.removeItem(chavePorLoja(idConfig));
    else localStorage.setItem(chavePorLoja(idConfig), JSON.stringify(pedidos));
  } catch {
    // Storage cheio ou bloqueado (aba anônima): o pedido segue acessível pela URL.
  }
  window.dispatchEvent(new Event(EVENTO_MUDANCA));
}

function vigentes(todos: PedidoLocal[], agora: number): PedidoLocal[] {
  return todos.filter((p) => agora - p.criadoEm <= VALIDADE_PEDIDO_LOCAL_MS).sort((a, b) => b.criadoEm - a.criadoEm);
}

/** Pedidos dentro da validade, do mais recente para o mais antigo. Descarta os vencidos do storage. */
export function listarPedidosLocais(idConfig: number, agora: number = Date.now()): PedidoLocal[] {
  const todos = ler(idConfig);
  const lista = vigentes(todos, agora);
  if (lista.length !== todos.length) gravar(idConfig, lista);
  return lista;
}

/** Registra (ou atualiza) o pedido. Pedido já vencido não entra — abrir um link antigo não o ressuscita. */
export function registrarPedidoLocal(
  idConfig: number,
  pedido: { id: string; shortId: string | null; dataCriacao: string },
  agora: number = Date.now(),
): void {
  const criadoEm = Date.parse(pedido.dataCriacao);
  if (Number.isNaN(criadoEm) || agora - criadoEm > VALIDADE_PEDIDO_LOCAL_MS) return;
  const atuais = listarPedidosLocais(idConfig, agora);
  const existente = atuais.find((p) => p.id === pedido.id);
  if (existente && existente.shortId === pedido.shortId) return;
  const novo: PedidoLocal = { ...existente, id: pedido.id, shortId: pedido.shortId, criadoEm };
  const lista = [novo, ...atuais.filter((p) => p.id !== pedido.id)]
    .sort((a, b) => b.criadoEm - a.criadoEm)
    .slice(0, MAXIMO_PEDIDOS_LOCAIS);
  gravar(idConfig, lista);
}

export function marcarPedidoLocalFinalizado(idConfig: number, id: string): void {
  const atuais = ler(idConfig);
  if (!atuais.some((p) => p.id === id && !p.finalizado)) return;
  gravar(idConfig, atuais.map((p) => (p.id === id ? { ...p, finalizado: true } : p)));
}

/** Pedido que a API não conhece mais (404) sai da lista. */
export function removerPedidoLocal(idConfig: number, id: string): void {
  const atuais = ler(idConfig);
  if (!atuais.some((p) => p.id === id)) return;
  gravar(idConfig, atuais.filter((p) => p.id !== id));
}

/** A API disse que o pedido não existe (nesta loja). Outro 404 (ex.: loja não resolvida) não apaga nada. */
export function pedidoNaoEncontrado(erro: unknown): boolean {
  return (erro as { data?: { codigo?: unknown } } | null | undefined)?.data?.codigo === 'PEDIDO_NAO_ENCONTRADO';
}

function assinar(aoMudar: () => void): () => void {
  window.addEventListener(EVENTO_MUDANCA, aoMudar);
  // Outra aba da mesma loja (ex.: o pedido foi feito numa aba e o cardápio está aberto em outra).
  window.addEventListener('storage', aoMudar);
  return () => {
    window.removeEventListener(EVENTO_MUDANCA, aoMudar);
    window.removeEventListener('storage', aoMudar);
  };
}

/** Lista reativa dos pedidos locais da loja; loja ainda não carregada (`null`) = lista vazia. */
export function usePedidosLocais(idConfig: number | null | undefined): PedidoLocal[] {
  // Snapshot em string: o useSyncExternalStore compara por referência, e a lista é recriada a cada leitura.
  // Só lê (roda durante o render): o vencido sai do storage na próxima escrita ou listagem.
  const snapshot = useCallback(
    () => (idConfig == null ? '[]' : JSON.stringify(vigentes(ler(idConfig), Date.now()))),
    [idConfig],
  );
  const json = useSyncExternalStore(assinar, snapshot, () => '[]');
  return JSON.parse(json) as PedidoLocal[];
}
